import React, { useEffect, useRef, useState } from "react";
import { initSocket } from "../initSocket";
import { Box, Button, HStack, Text, Badge } from "@chakra-ui/react";

type LatLng = { lat: number; lng: number; };

function haversine(a: LatLng, b: LatLng) {
    const toRad = (d: number) => (d * Math.PI) / 180;
    const R = 6371e3;
    const dLat = toRad(b.lat - a.lat);
    const dLng = toRad(b.lng - a.lng);
    const sLat1 = toRad(a.lat), sLat2 = toRad(b.lat);
    const x =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(sLat1) * Math.cos(sLat2) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(x)); // meters
}

export default function DriverTracker({ tripId }: { tripId: string; }) {
    const [connected, setConnected] = useState(false);
    const [watching, setWatching] = useState(false);
    const [last, setLast] = useState<LatLng | null>(null);
    const [speed, setSpeed] = useState<number>(0);
    const socketRef = useRef<any>(null);
    const watchIdRef = useRef<number | null>(null);
    const lastTimeRef = useRef<number | null>(null);

    useEffect(() => {
        let active = true;
        (async () => {
            const socket = await initSocket(); // ✅ fresh token + auto-refresh
            if (!active) return;
            socketRef.current = socket;
            socket.on("connect", () => setConnected(true));
            socket.on("disconnect", () => setConnected(false));
            socket.emit("joinTripRoom", { tripId });
        })();
        return () => {
            active = false;
        };
    }, [tripId]);

    const start = () => {
        if (!navigator.geolocation) {
            alert("Geolocation not supported. Use the simulator instead.");
            return;
        }
        if (watchIdRef.current != null) return;

        const id = navigator.geolocation.watchPosition(
            (pos) => {
                const { latitude: lat, longitude: lng } = pos.coords;
                const now = Date.now();
                const cur: LatLng = { lat, lng };

                if (last && lastTimeRef.current) {
                    const dtSec = (now - lastTimeRef.current) / 1000;
                    const dMeters = haversine(last, cur);
                    const kph = (dMeters / 1000) / (dtSec / 3600);
                    setSpeed(Math.round(kph));
                }

                lastTimeRef.current = now;
                setLast(cur);
                socketRef.current?.emit("locationUpdate", { tripId, lat, lng, speed });
            },
            (err) => console.error("gps error", err),
            { enableHighAccuracy: true, maximumAge: 1000, timeout: 10000 }
        );

        watchIdRef.current = id;
        setWatching(true);
    };

    const stop = () => {
        if (watchIdRef.current != null) {
            navigator.geolocation.clearWatch(watchIdRef.current);
            watchIdRef.current = null;
            setWatching(false);
        }
    };

    return (
        <Box p={4}>
            <HStack spacing={4}>
                <Badge colorScheme={connected ? "green" : "red"}>
                    {connected ? "Socket: Connected" : "Socket: Disconnected"}
                </Badge>
                <Badge colorScheme={watching ? "blue" : "gray"}>
                    {watching ? "GPS: Tracking" : "GPS: Idle"}
                </Badge>
                <Text>Speed: {speed} km/h</Text>
            </HStack>
            <HStack mt={3} spacing={3}>
                <Button colorScheme="blue" onClick={start} isDisabled={watching}>
                    Start GPS
                </Button>
                <Button onClick={stop} isDisabled={!watching}>
                    Stop GPS
                </Button>
            </HStack>
        </Box>
    );
}
